import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Formik } from 'formik';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAuth } from 'utilities/base-auth';
import { Spinner, Tabs, Toast } from '@agconnections/grow-ui';
import { organization as organizationApi } from 'utilities/api';
import { Context } from 'components/Store';
import { parseServerError } from 'helpers/errorHelpers';
import catchCancel from 'helpers/catchCancel';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import { paths } from 'routes/paths';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import formatAmplitudeError from 'utilities/amplitude/helpers';
import TopHeader from './components/TopHeader';
import InvoiceDetailsTab from './components/InvoiceDetailsTab';
import InvoiceProductsTab from './components/InvoiceProductsTab';

import schema from './schema';
import useInvoiceData from './hooks/useInvoiceData';
import { defaultValues, fetchInvoiceById } from './helpers/fetchInvoiceById';

const buildSaveInvoiceEventProperties = (
  result,
  apiCallLengthMs,
  lastError,
  numberOfProducts,
  isDuplicateScreen
) => ({
  'Save Invoice result': `${result} (${apiCallLengthMs} ms)`,
  Error:
    result === 'Error' && lastError ? formatAmplitudeError(lastError) : 'N/A',
  'Number of products': numberOfProducts,
  'Record source': isDuplicateScreen ? 'Invoice' : 'N/A'
});

// Duplicating an invoice must not carry over the source's product-line ids.
const stripProductIds = invoiceProducts =>
  invoiceProducts.map(product => {
    // eslint-disable-next-line no-unused-vars
    const { id: _id, ...rest } = product;
    return rest;
  });

const Invoice = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(defaultValues);
  const [loading, setLoading] = useState(true);
  const [, dispatch] = useContext(Context);
  const [toastRenderContents, setToastRenderContents] = useState(null);
  const [toastHasNotBeenClosed, setToastHasNotBeenClosed] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [financialAccess, setFinancialAccess] = useState();
  const [selectedCropSeasonIds, setSelectedCropSeasonIds] = useState(
    JSON.parse(localStorage.getItem('selectedCropSeasons')) || null
  );
  const updateSelectedCropSeasons = () => {
    setSelectedCropSeasonIds(
      JSON.parse(localStorage.getItem('selectedCropSeasons')) || null
    );
  };
  const history = useHistory();
  const location = useLocation();
  const isDuplicate = location.pathname?.includes('duplicate') || false;
  let invID = '';
  if (id?.length > 0) invID = id;
  else {
    invID = 'invoice';
  }
  const isCreateScreen = id === 'invoice' || false;
  const isEdit = (id === invoice?.id && !isDuplicate) || false;
  const isDuplicateScreen = isDuplicate || false;
  const {
    saveInvoice,
    updateInvoice,
    error,
    setError,
    saving,
    getLastError
  } = useInvoiceData(invID);
  const { sendEventToAmplitude, events } = useContext(AmplitudeContext);

  useEffect(() => {
    fetchInvoiceById(invID, setInvoice, isDuplicateScreen, setLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, invID]);

  const getSingleMember = useCallback(async () => {
    const { promise } = organizationApi.fetch();
    await promise
      .then(({ data }) => {
        const filterMember = data?.members?.filter(
          eachMember => eachMember.email === user.email
        )[0];
        setSelectedMember(filterMember);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      });
  }, [dispatch, user.email]);

  useEffect(() => {
    if (!selectedMember) {
      getSingleMember();
    }

    if (
      selectedMember?.role === 'Full control' &&
      !Object.prototype.hasOwnProperty.call(selectedMember, 'financialAccess')
    ) {
      setFinancialAccess('none');
    } else {
      setFinancialAccess(selectedMember?.financialAccess);
    }
  }, [selectedMember, getSingleMember]);

  const onSubmit = async values => {
    const invDateEpoch = Date.parse(values?.invoiceDate);
    const invDueDateEpoch = Date.parse(values?.invoiceDueDate);
    const seasonsArr = selectedCropSeasonIds?.map(season => {
      return { seasonId: season, name: '' };
    });
    delete values.totalQuantityUnit; // eslint-disable-line no-param-reassign
    if (values?.invoiceProducts?.length > 0)
      values.invoiceProducts.forEach(v => {
        delete v.manufacturerName; // eslint-disable-line no-param-reassign
      });

    const invoiceValues = {
      ...values,
      invoiceDueDateEpoch: invDueDateEpoch,
      invoiceDateEpoch: invDateEpoch,
      cropSeasons: seasonsArr,
      flagIds: values?.flagIds?.length > 0 ? values?.flagIds?.split(',') : []
    };

    // Only remove id from each product if duplicating
    if (isDuplicateScreen && invoiceValues.invoiceProducts?.length > 0) {
      invoiceValues.invoiceProducts = stripProductIds(
        invoiceValues.invoiceProducts
      );
    }

    if (isDuplicateScreen) {
      invoiceValues.trackingId = '';
    }

    delete invoiceValues.invoiceDate;
    delete invoiceValues.invoiceDueDate;
    delete invoiceValues.cropSeason;
    delete invoiceValues.cropsSeasons;
    if (values?.id === undefined || isDuplicateScreen) delete invoiceValues.id;
    let success = '';
    if (values?.id?.length > 0 && !isDuplicateScreen) {
      success = await updateInvoice(invoiceValues);
    } else {
      const startedAt = performance.now();
      success = await saveInvoice(invoiceValues);
      const apiCallLengthMs = Math.round(performance.now() - startedAt);
      sendEventToAmplitude(
        events.epic.Invoices.saveInvoice,
        buildSaveInvoiceEventProperties(
          success ? 'Success' : 'Error',
          apiCallLengthMs,
          getLastError(),
          values?.invoiceProducts?.length || 0,
          isDuplicateScreen
        )
      );
    }
    if (success) {
      setToastRenderContents('Save Succeeded');
      history.push(paths.invoices);
    }
  };
  return (
    <>
      <div className="w-full h-full" data-testid="invoices">
        <Breadcrumb
          disabled={isCreateScreen || isEdit || isDuplicateScreen}
          hideCropSeasonDropdown={false}
          onCropSeasonSelect={updateSelectedCropSeasons}
        >
          <Breadcrumb.Item
            title="Invoices"
            value="All Invoices"
            to={paths.invoices}
          />
          <Breadcrumb.Item
            title="Invoices"
            value={
              isCreateScreen || isDuplicateScreen
                ? 'Create Invoice'
                : 'Edit Invoice'
            }
            isLast
          />
        </Breadcrumb>
        {!loading ? (
          <>
            {error && (
              <Toast icon="error" onClose={() => setError('')}>
                {error}
              </Toast>
            )}
            <Formik
              initialValues={invoice}
              validationSchema={schema}
              onSubmit={onSubmit}
            >
              {({ submitForm }) => {
                return (
                  <>
                    <TopHeader
                      onSubmit={submitForm}
                      memberRole={selectedMember?.role}
                      memberFinancialAccess={financialAccess}
                      isSaving={saving}
                    />
                    {loading ? (
                      <Spinner />
                    ) : (
                      <div
                        data-testid="tabs-content"
                        style={{ height: 'calc(100% - 58px)' }}
                      >
                        {toastHasNotBeenClosed ? (
                          <Toast
                            onClose={() => {
                              setToastHasNotBeenClosed(false);
                            }}
                          >
                            {toastRenderContents}
                          </Toast>
                        ) : null}
                        <div
                          className="px-6 pt-4 -mx-4 bg-white"
                          style={{ height: 'calc(100% - 16px)' }}
                        >
                          <Tabs>
                            <Tabs.Tab label="Details">
                              <InvoiceDetailsTab
                                memberRole={selectedMember?.role}
                                memberFinancialAccess={financialAccess}
                              />
                            </Tabs.Tab>
                            <Tabs.Tab label="Products">
                              <InvoiceProductsTab
                                memberRole={selectedMember?.role}
                                memberFinancialAccess={financialAccess}
                              />
                            </Tabs.Tab>
                          </Tabs>
                        </div>
                      </div>
                    )}
                  </>
                );
              }}
            </Formik>
          </>
        ) : (
          <Spinner />
        )}
      </div>
    </>
  );
};

export default Invoice;
